import React, { FC, ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Drawer from "@mui/material/Drawer";
import { motion, Reorder } from "framer-motion";
import { usePopper } from "react-popper";
import styled from "styled-components";

import { primary } from "@formant/ui/component/common/Colors";

import { getPortalElement } from "../../utils/getPortalElement";
import { Label } from "../JsonSchemaForm/Label";
import { Header } from "./Header";
import { ListButton } from "./ListButton";
import { Scrollable } from "./Scrollable";

export interface IListFormProps {
    label: string;
    description?: string;
    link?: string;
    items: string[];
    popup: ReactNode;

    isLoadingOptions?: boolean;
    drawer: (id: string) => ReactNode;
    handleAdd?: () => Promise<void>;
    onDelete: (item: string) => void;
    onReorder: (items: string[]) => void;
}

export const ListForm: FC<IListFormProps> = props => {
    const {
        label,
        description,
        link,
        items,
        onDelete,
        onReorder,
        popup,
        handleAdd,
        isLoadingOptions,
        drawer,
    } = props;

    const [visible, setVisible] = React.useState<boolean>(false);
    const [drawerVisible, setDrawerVisible] = React.useState<
        string | undefined
    >(undefined);
    const [refElement, setRefElement] = useState<HTMLElement | null>(null);
    const [popElement, setPopElement] = useState<HTMLElement | null>(null);
    const [drawerElement, setDrawerElement] = useState<HTMLElement | null>(
        null,
    );

    const { styles, attributes } = usePopper(refElement, popElement, {
        placement: "top-end",
        modifiers: [{ name: "offset", options: { offset: [20, -30] } }],
    });

    const { styles: drawerStyles, attributes: drawerAttributes } = usePopper(
        refElement,
        drawerElement,
        {
            placement: "top-end",
            modifiers: [{ name: "offset", options: { offset: [40, -30] } }],
        },
    );

    useEffect(() => {
        if (popElement) {
            document.addEventListener("click", closeModal);
            return () => {
                document.removeEventListener("click", closeModal);
            };
        }
    }, [popElement]);

    const closeModal = (event: MouseEvent | TouchEvent) => {
        if (refElement && refElement.contains(event.target as Node)) {
            return;
        }
        if (!popElement?.contains(event.target as Node)) {
            setVisible(false);
        }
    };

    const onAdd = async () => {
        if (handleAdd) {
            await handleAdd();
        }
        setVisible(true);
    };

    const popupElement = visible ? (
        <div ref={setPopElement} style={styles.popper} {...attributes.popper}>
            {popup}
        </div>
    ) : null;

    const drawerPopupElement = drawerVisible ? (
        <div
            ref={setDrawerElement}
            style={drawerStyles.popper}
            {...drawerAttributes.popper}
        >
            <StyledDrawer
                anchor="right"
                open={!!drawerVisible}
                onClose={() => setDrawerVisible(undefined)}
                hideBackdrop={true}
            >
                <Scroll dark={true}>
                    <Header
                        title={`Edit ${drawerVisible}`}
                        hideMenu={true}
                        onBack={() => setDrawerVisible(undefined)}
                        setDocumentTitle={false}
                    />
                    <SmallFormContainer>
                        {drawerVisible && drawer(drawerVisible)}
                    </SmallFormContainer>
                </Scroll>
            </StyledDrawer>
        </div>
    ) : null;

    return (
        <Container ref={setRefElement}>
            <Label
                value={label}
                description={description}
                color={primary.silver}
                link={link}
            />
            <ListContainer>
                <Reorder.Group
                    axis="y"
                    values={items}
                    onReorder={onReorder}
                    layoutScroll={true}
                >
                    {items.map((value, newIndex) => (
                        <Reorder.Item
                            key={value}
                            drag="y"
                            value={value}
                            whileDrag={{ zIndex: 2, position: "relative" }}
                        >
                            <motion.div onTap={() => {}}>
                                <DraggableListButton
                                    kebab={true}
                                    index={newIndex}
                                    icon="chevron-right"
                                    label={value}
                                    onClick={() => setDrawerVisible(value)}
                                    onDelete={onDelete}
                                />
                            </motion.div>
                        </Reorder.Item>
                    ))}
                </Reorder.Group>

                <AddListButton
                    onClick={onAdd}
                    items={items.length}
                    empty={true}
                    loading={isLoadingOptions}
                    label="Add..."
                    onDelete={() => {}}
                />
            </ListContainer>

            {createPortal(popupElement, getPortalElement())}
            {createPortal(drawerPopupElement, getPortalElement())}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    z-index: 0;
`;

const StyledDrawer = styled(Drawer)`
    .MuiPaper-root {
        overflow: hidden;
        background-color: ${primary.module};
        background-image: none;
        width: 22rem;
        z-index: 100 !important;
    }
`;

export const SmallFormContainer = styled.div`
    padding: 2rem;
    padding-top: 0rem;

    select {
        max-width: 17.5rem;
    }
`;

const AddListButton = styled(ListButton)<{ items: number }>`
    position: relative;
    z-index: 0;
    margin-bottom: 0rem !important;

    border-radius: ${props =>
        props.items > 0
            ? "0rem 0rem 0.1875rem 0.1875rem"
            : "0.1875rem 0.1875rem 0.1875rem 0.1875rem"};
`;

const DraggableListButton = styled(ListButton)<{ index: number }>`
    border-radius: ${props =>
        props.index === 0 ? " 0.1875rem 0.1875rem 0rem 0rem " : "0rem"};

    span {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
`;

const ListContainer = styled(Container)`
    background-color: ${primary.steel01};
    width: 17.5rem;
    border-radius: 0.1875rem;

    ul {
        padding: 0rem;
        margin: 0rem;
        list-style: none;
    }
`;

const Scroll = styled(Scrollable)`
    max-height: 90vh;

    h1 {
        padding-left: 0.93rem;
    }
`;
